import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtMXN } from "../lib/api";
import Topbar from "../components/Topbar";
import SupplierSupplyModal from "../components/SupplierSupplyModal";
import { Plus, Trash2, AlertTriangle, Boxes, Send, Lock, Building2, Layers, Package, UserCheck, ClipboardList } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "../lib/auth";

export default function PurchaseOrderNew() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const fromDraftId = searchParams.get("fromDraft");
  const [draft, setDraft] = useState(null);
  const isCorp = user?.role === "corporate_admin";
  const authorizedBranches = user?.branches_authorized || (user?.branch_id ? [user.branch_id] : []);
  const isMulti = authorizedBranches.length > 1;
  const isBranchMgr = user?.role === "branch_manager";
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(isMulti ? "" : authorizedBranches[0] || "");
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [costCenters, setCostCenters] = useState([]);
  const [selectedCC, setSelectedCC] = useState("");
  const [supplierSupplies, setSupplierSupplies] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [items, setItems] = useState([]);
  const [ivaRate, setIvaRate] = useState(16);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [qtyFlagged, setQtyFlagged] = useState(null);   // líneas con cantidad fuera de lo normal
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/inventory").then((r) => setInventory(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/cost-centers/allowed-for-me").then((r) => setCostCenters(r.data)).catch(() => setCostCenters([]));
  }, []);

  useEffect(() => {
    if (!fromDraftId) return;
    api.get(`/ops/purchase-order-drafts/${fromDraftId}`).then((r) => {
      setDraft(r.data);
      if (r.data.branch_id) setSelectedBranch(r.data.branch_id);
      setNotes(
        `Requisición aprobada: ${r.data.item_description} (cant. ${r.data.quantity} ${r.data.unit || ""}). ` +
        (r.data.proveedor_sugerido ? `Proveedor sugerido: ${r.data.proveedor_sugerido}. ` : "") +
        (r.data.notes || "")
      );
    }).catch(() => toast.error("No se pudo cargar la requisición de origen"));
  }, [fromDraftId]);

  const loadSupplierSupplies = () => {
    if (!selectedSupplier) { setSupplierSupplies([]); return; }
    api.get(`/suppliers/${selectedSupplier}/supplies`, { params: { active_only: true, include_supply_details: true } })
      .then((r) => setSupplierSupplies(r.data));
  };

  useEffect(() => {
    if (!selectedSupplier) { setSupplierSupplies([]); return; }
    loadSupplierSupplies();
  }, [selectedSupplier]); // eslint-disable-line react-hooks/exhaustive-deps

  const stockFor = (supply_id) => {
    const s = inventory.find((i) => i.supply_id === supply_id);
    return s ? s.quantity_on_hand : null;
  };

  const catalogPriceFor = (supply_id) => {
    const rel = supplierSupplies.find((s) => s.supply_id === supply_id);
    return rel ? rel.unit_price : null;
  };

  const addItem = () => {
    setItems([...items, { supply_id: "", quantity: 1, unit_price: 0, update_catalog_price: false }]);
  };

  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    if (patch.supply_id) {
      const rel = supplierSupplies.find((s) => s.supply_id === patch.supply_id);
      if (rel) {
        next[idx].unit_price = rel.unit_price;
      }
    }
    setItems(next);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const subtotal = items.reduce((a, b) => a + (b.quantity || 0) * (b.unit_price || 0), 0);
  const tax = Math.round(((subtotal * ivaRate) / 100) * 100) / 100;
  const total = subtotal + tax;

  const doSubmit = async () => {
    setQtyFlagged(null);
    setSubmitting(true);
    try {
      await api.post("/purchase-orders", {
        supplier_id: selectedSupplier,
        branch_id: selectedBranch || undefined,
        cost_center_code: selectedCC,
        items: items.map((i) => ({
          supply_id: i.supply_id,
          quantity: Number(i.quantity),
          unit_price: Number(i.unit_price),
          update_catalog_price: !!i.update_catalog_price,
        })),
        iva_rate: ivaRate,
        notes,
        origin_draft_id: fromDraftId || undefined,
      });
      toast.success(t("po.sentToApproval"));
      navigate("/ocs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("po.errorCreate"));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (isMulti && !selectedBranch) {
      toast.error(t("po.selectBranch"));
      return;
    }
    if (!selectedCC) {
      toast.error(t("po.costCenterRequired"));
      return;
    }
    if (!selectedSupplier) {
      toast.error(t("po.selectSupplier"));
      return;
    }
    if (items.length === 0 || items.some((i) => !i.supply_id || !i.quantity)) {
      toast.error(t("po.addAtLeastOneItem"));
      return;
    }
    // Antes de enviar: ¿alguna cantidad se sale de lo normal para esta sucursal?
    // Si sí, se pide una confirmación explícita con el número a la vista.
    setChecking(true);
    try {
      const r = await api.post("/purchase-orders/quantity-check", {
        branch_id: selectedBranch || undefined,
        items: items.map((i, idx) => ({ supply_id: i.supply_id, quantity: Number(i.quantity), line: idx })),
      });
      const flagged = r.data?.flagged || [];
      if (flagged.length > 0) {
        setQtyFlagged(flagged);
        return;
      }
    } catch (e) {
      // Si el chequeo falla, no bloqueamos el alta de la OC.
    } finally {
      setChecking(false);
    }
    await doSubmit();
  };

  const approvedSuppliers = suppliers.filter((s) => s.status === "approved");

  // Sprint (Feb 2026) — Operaciones por sucursal: si el proveedor tiene
  // vendedor/cuenta configurados para la sucursal elegida, se lo muestra
  // arriba de la OC (se guarda como snapshot al crear, del lado del backend).
  const supplierObj = suppliers.find((s) => s.id === selectedSupplier);
  const hasBranchOps = (supplierObj?.branch_operations || []).length > 0;
  const matchedOp = hasBranchOps
    ? (supplierObj.branch_operations || []).find((op) => op.branch_id === selectedBranch && op.active !== false)
    : null;

  return (
    <>
      <Topbar title={t("po.newTitle")} subtitle={t("po.newSubtitle")} />
      <div className="p-6 max-w-5xl space-y-5">
        {draft && (
          <div data-testid="po-from-draft-banner" className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-2xl px-5 py-3.5">
            <ClipboardList className="w-5 h-5 text-indigo-600 flex-shrink-0" />
            <div>
              <span className="text-sm font-bold text-indigo-900">Creando OC desde requisición aprobada</span>
              <span className="text-xs text-indigo-700 ml-2">
                {draft.item_description} · cant. {draft.quantity} {draft.unit} · monto est. {fmtMXN(draft.monto_estimado)}
              </span>
            </div>
          </div>
        )}
        {isMulti && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <label className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A] flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> {t("po.branch")}
            </label>
            <select
              data-testid="po-branch-select"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
            >
              <option value="">{t("po.chooseBranch")}</option>
              {(() => {
                const userBranch = branches.find((b) => b.id === user?.branch_id);
                const userIsCorp = !!(userBranch && (userBranch.code === "CRP" || userBranch.is_corporate));
                return branches
                  .filter((b) => authorizedBranches.includes(b.id))
                  .filter((b) => !(isBranchMgr && !userIsCorp && (b.code === "CRP" || b.is_corporate)))
                  .map((b) => (
                    <option key={b.id} value={b.id}>{b.code ? `${b.code} — ${b.name}` : b.name}</option>
                  ));
              })()}
            </select>
            <p className="text-[11px] text-slate-500 mt-2">{t("po.branchHint")}</p>
          </div>
        )}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <label className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A] flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" /> {t("po.costCenter")}
          </label>
          {costCenters.length === 0 ? (
            <div
              data-testid="po-no-cost-center"
              className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5"
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {t("po.noCostCenter")}
            </div>
          ) : (
            <select
              data-testid="po-cost-center-select"
              value={selectedCC}
              onChange={(e) => setSelectedCC(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
            >
              <option value="">{t("po.selectCostCenter")}</option>
              {costCenters.map((cc) => (
                <option key={cc.code} value={cc.code}>{cc.code} — {cc.name}</option>
              ))}
            </select>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <label className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A]">{t("po.supplier")}</label>
          <select
            data-testid="po-supplier-select"
            value={selectedSupplier || ""}
            onChange={(e) => { setSelectedSupplier(e.target.value); setItems([]); }}
            className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
          >
            <option value="">{t("po.chooseSupplier")}</option>
            {approvedSuppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.business_name} · {s.credit_days}d {t("common.creditDays")}</option>
            ))}
          </select>
        </div>

        {selectedSupplier && selectedBranch && hasBranchOps && (
          matchedOp ? (
            <div data-testid="po-vendor-banner" className="flex items-start gap-2.5 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
              <UserCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                <Trans
                  i18nKey="po.vendorAssignedBanner"
                  values={{ branch: branches.find((b) => b.id === selectedBranch)?.name || "", vendor: matchedOp.vendor_name || "—", email: matchedOp.orders_email || matchedOp.vendor_email || "" }}
                  components={{ strong: <strong /> }}
                />
              </span>
            </div>
          ) : (
            <div data-testid="po-vendor-missing-banner" className="flex items-start gap-2.5 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t("po.vendorMissingBanner", { branch: branches.find((b) => b.id === selectedBranch)?.name || "" })}</span>
            </div>
          )
        )}

        {selectedSupplier && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-lg">{t("po.itemsOfOrder")}</h2>
                <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                  <Boxes className="w-3 h-3" /> {t("po.posSync")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isCorp && (
                  <button
                    data-testid="po-add-product-btn"
                    onClick={() => setAddProductOpen(true)}
                    className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold flex items-center gap-1.5"
                  >
                    <Package className="w-3.5 h-3.5" /> {t("po.addProductToSupplier")}
                  </button>
                )}
                {supplierSupplies.length > 0 && (
                  <button
                    data-testid="po-add-item-btn"
                    onClick={addItem}
                    className="px-3 py-2 bg-[#1A1A1A] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-black"
                  >
                    <Plus className="w-3.5 h-3.5" /> {t("po.addItem")}
                  </button>
                )}
              </div>
            </div>

            {supplierSupplies.length === 0 ? (
              <div data-testid="po-no-products-empty" className="text-center py-10">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Package className="w-7 h-7 text-slate-400" />
                </div>
                <h3 className="font-display font-bold text-base text-[#1A1A1A]">{t("po.noProductsTitle")}</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                  {isCorp ? t("po.noProductsBodyCorp") : t("po.noProductsBodyBranch")}
                </p>
                {isCorp && (
                  <button data-testid="po-empty-add-product-btn" onClick={() => setAddProductOpen(true)}
                    className="mt-5 btn-primary-chizy px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2">
                    <Plus className="w-4 h-4" /> {t("po.addProductToSupplier")}
                  </button>
                )}
              </div>
            ) : (
            <>
            {!isCorp && (
              <div
                data-testid="po-price-locked-notice"
                className="mb-4 flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
              >
                <Lock className="w-3.5 h-3.5 text-slate-500" />
                {t("po.priceLockNotice")}
              </div>
            )}

            {items.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-400">
                {t("po.noItemsHint")}
              </div>
            ) : (
              <div className="space-y-2.5">
                <AnimatePresence>
                  {items.map((item, idx) => {
                    const stock = stockFor(item.supply_id);
                    const warn = stock !== null && item.quantity > stock * 3;
                    const catalogPrice = catalogPriceFor(item.supply_id);
                    const overridden = isCorp && catalogPrice !== null && Math.abs((item.unit_price || 0) - catalogPrice) > 0.001;
                    return (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="grid grid-cols-12 gap-2 items-start bg-slate-50 rounded-xl p-3"
                      >
                        <div className="col-span-12 sm:col-span-5">
                          <select
                            data-testid={`po-item-supply-${idx}`}
                            value={item.supply_id}
                            onChange={(e) => updateItem(idx, { supply_id: e.target.value })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                          >
                            <option value="">{t("po.chooseSupply")}</option>
                            {supplierSupplies.map((s) => (
                              <option key={s.supply_id} value={s.supply_id}>{s.supply.name} · {fmtMXN(s.unit_price)}/{s.supply.unit}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">{t("po.stockTitle")}</div>
                          <div className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-mono text-slate-500">
                            {stock !== null ? stock.toFixed(1) : "—"}
                          </div>
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">{t("po.qtyTitle")}</div>
                          <input
                            data-testid={`po-item-qty-${idx}`}
                            type="number"
                            min="0"
                            step="0.1"
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, { quantity: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                          />
                        </div>
                        <div className="col-span-3 sm:col-span-2">
                          <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5 flex items-center gap-1">
                            {t("po.priceTitle")}
                            {!isCorp && <Lock className="w-2.5 h-2.5 text-slate-400" />}
                          </div>
                          {isCorp ? (
                            <input
                              data-testid={`po-item-price-${idx}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.supply_id ? (item.unit_price ?? 0) : ""}
                              disabled={!item.supply_id}
                              onChange={(e) => updateItem(idx, { unit_price: parseFloat(e.target.value) || 0 })}
                              className={`w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00] ${overridden ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}
                            />
                          ) : (
                            <input
                              data-testid={`po-item-price-${idx}`}
                              type="number"
                              readOnly
                              disabled
                              value={catalogPrice !== null ? catalogPrice : 0}
                              title={t("po.catalogPriceTitle")}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono bg-slate-100 text-slate-600 cursor-not-allowed"
                            />
                          )}
                        </div>
                        <div className="col-span-1">
                          <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5 invisible">x</div>
                          <button
                            data-testid={`po-item-remove-${idx}`}
                            onClick={() => removeItem(idx)}
                            className="w-8 h-8 rounded-lg hover:bg-red-50 text-red-500 flex items-center justify-center"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {overridden && (
                          <div className="col-span-12 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{t("po.priceOverrideHint", { catalog: fmtMXN(catalogPrice) })}</span>
                            <label className="ml-auto flex items-center gap-1.5 cursor-pointer font-semibold">
                              <input
                                data-testid={`po-item-update-catalog-${idx}`}
                                type="checkbox"
                                checked={!!item.update_catalog_price}
                                onChange={(e) => updateItem(idx, { update_catalog_price: e.target.checked })}
                                className="rounded"
                              />
                              {t("po.updateCatalogPrice")}
                            </label>
                          </div>
                        )}
                        {warn && (
                          <div className="col-span-12 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <Trans
                              i18nKey="po.stockWarning"
                              values={{ qty: item.quantity, stock: stock?.toFixed(1) }}
                              components={{ strong: <strong /> }}
                            />
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}

            {items.length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-200 flex justify-end">
                <div className="text-right space-y-1">
                  <div className="flex justify-end gap-8 text-sm text-slate-500">
                    <span>{t("common.subtotal")}</span><span className="font-mono">{fmtMXN(subtotal)}</span>
                  </div>
                  <div className="flex justify-end gap-8 text-sm text-slate-500 items-center">
                    <span>{t("common.tax")}</span>
                    <span className="flex items-center gap-1.5">
                      <input
                        data-testid="po-iva-rate"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={ivaRate}
                        onChange={(e) => setIvaRate(Number(e.target.value))}
                        className="w-14 px-2 py-0.5 border border-slate-200 rounded-lg text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                      />
                      <span className="text-xs text-slate-400">%</span>
                      <span className="font-mono">{fmtMXN(tax)}</span>
                    </span>
                  </div>
                  <div className="flex justify-end gap-8 text-xl font-display font-extrabold text-[#1A1A1A]">
                    <span>{t("common.total")}</span><span className="font-mono">{fmtMXN(total)}</span>
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </motion.div>
        )}

        {selectedSupplier && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <label className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A]">{t("po.optionalNotes")}</label>
            <textarea
              data-testid="po-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t("po.notesPlaceholder")}
              className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => navigate("/ocs")}
            className="px-5 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50"
          >
            {t("common.cancel")}
          </button>
          <button
            data-testid="po-submit-btn"
            onClick={submit}
            disabled={!selectedSupplier || !selectedCC || (isMulti && !selectedBranch) || items.length === 0 || submitting || checking}
            className="btn-primary-chizy px-5 py-3 rounded-xl flex items-center gap-2 disabled:opacity-50"
          >
            <Send className="w-4 h-4" strokeWidth={2.5} />
            {submitting || checking ? t("po.sending") : t("po.sendToApproval")}
          </button>
        </div>
      </div>

      {qtyFlagged && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="po-qty-confirm-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h2 className="font-display font-bold text-lg text-[#1A1A1A]">
                  Confirma estas cantidades antes de enviar
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {qtyFlagged.length === 1 ? "Esta línea pide" : "Estas líneas piden"} bastante más de lo
                  habitual. El detalle de la comparación está debajo. Puedes corregir la cantidad acá mismo.
                </p>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {qtyFlagged.map((f, i) => {
                const idx = f.line ?? items.findIndex((it) => it.supply_id === f.supply_id);
                const current = idx >= 0 ? items[idx]?.quantity : f.quantity;
                const veryHigh = f.quantity_flag?.level === "muy_alto";
                return (
                  <div
                    key={`${f.supply_id}-${i}`}
                    data-testid={`po-qty-flag-line-${i}`}
                    className={`rounded-xl border p-4 ${veryHigh ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-[#1A1A1A]">{f.supply_name}</div>
                        <div className={`text-xs mt-0.5 ${veryHigh ? "text-red-800" : "text-amber-800"}`}>
                          {f.quantity_flag?.reason}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <input
                          data-testid={`po-qty-flag-input-${i}`}
                          type="number"
                          min="0"
                          step="0.1"
                          value={current ?? ""}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (idx >= 0) updateItem(idx, { quantity: val });
                            setQtyFlagged((prev) =>
                              prev.map((p, pi) => (pi === i ? { ...p, quantity: val } : p))
                            );
                          }}
                          className="w-24 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-mono text-right font-bold"
                        />
                        <span className="text-xs text-slate-500">{f.unit}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                data-testid="po-qty-flag-fix-btn"
                onClick={() => { setQtyFlagged(null); setChecking(false); }}
                className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50"
              >
                Volver a corregir
              </button>
              <button
                data-testid="po-qty-flag-confirm-btn"
                onClick={doSubmit}
                disabled={submitting}
                className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" strokeWidth={2.5} />
                {submitting ? t("po.sending") : "Sí, es correcto — enviar a aprobación"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {addProductOpen && selectedSupplier && (
        <SupplierSupplyModal
          supplierId={selectedSupplier}
          existingSupplyIds={supplierSupplies.map((s) => s.supply_id)}
          onClose={() => setAddProductOpen(false)}
          onLinked={loadSupplierSupplies}
        />
      )}
    </>
  );
}
